import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Sparkles, Flame } from "lucide-react";
import { api, optimg } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { MaskLine, Reveal } from "@/components/Reveal";

const FESTIVALS = {
  holi: {
    name: "Holi",
    tagline: "Colour their world.",
    intro: "A riot of handmade colour — vibrant resin art, playful crochet and joyful little gifts to celebrate the festival of colours with the people you love.",
    hero: "https://images.unsplash.com/photo-1603228254119-e6a4d095dc59?crop=entropy&cs=srgb&fm=jpg&q=90",
    accent: "#D17A4D",
    marquee: ["Happy Holi", "Handmade with love", "Festival of colours", "Colour their world"],
    chips: ["Colourful resin", "Playful crochet", "Bright keepsakes"],
  },
  "valentines-day": {
    name: "Valentine's Day",
    tagline: "Say it by hand.",
    intro: "Nothing says love like something made by hand — romantic bouquets, resin keepsakes and heartfelt albums for the one who holds your heart.",
    hero: "https://images.unsplash.com/photo-1615654673360-29898e4f857f?crop=entropy&cs=srgb&fm=jpg&q=90",
    accent: "#8C3D2B",
    marquee: ["Be my Valentine", "Handmade romance", "Made with love", "Say it by hand"],
    chips: ["Romantic bouquets", "Resin keepsakes", "Memory albums"],
  },
  "raksha-bandhan": {
    name: "Raksha Bandhan",
    tagline: "Tie a thread of love.",
    intro: "Celebrate the bond with a Rakhi gift as handmade as your memories together — crochet blooms, keepsakes and little treasures for your sibling.",
    hero: "https://images.unsplash.com/photo-1716400128984-3681f2005d22?crop=entropy&cs=srgb&fm=jpg&q=90",
    accent: "#8C3D2B",
    marquee: ["Happy Rakhi", "For your sibling", "Handmade keepsakes", "Tie a thread of love"],
    chips: ["Keepsakes", "Crochet blooms", "For siblings"],
  },
  "ganesh-chaturthi": {
    name: "Ganesh Chaturthi",
    tagline: "Blessings, handcrafted.",
    intro: "Welcome Bappa with handmade devotion — artisan pottery, resin decor and warm candles to make your celebration truly personal.",
    hero: "https://images.unsplash.com/photo-1598209437948-3f0ae8a0e45d?crop=entropy&cs=srgb&fm=jpg&q=90",
    accent: "#D17A4D",
    marquee: ["Ganpati Bappa Morya", "Handmade devotion", "Festive decor", "Blessings, handcrafted"],
    chips: ["Artisan pottery", "Festive decor", "Warm candles"],
  },
  navratri: {
    name: "Navratri",
    tagline: "Nine nights, handmade.",
    intro: "Dress the season in craft — handcrafted jewellery, festive blooms and ceramics to celebrate nine nights of colour, dance and devotion.",
    hero: "https://images.unsplash.com/photo-1617875216004-78f15839c578?crop=entropy&cs=srgb&fm=jpg&q=90",
    accent: "#8C3D2B",
    marquee: ["Happy Navratri", "Nine nights of colour", "Handmade jewels", "Dance the night"],
    chips: ["Handmade jewellery", "Festive blooms", "Ceramics"],
  },
  diwali: {
    name: "Diwali",
    tagline: "Light up their heart.",
    intro: "A handpicked Diwali edit — hand-poured candle diyas, warm ceramics and everlasting blooms to gift the ones who light up your life.",
    hero: "https://images.unsplash.com/photo-1577083753695-e010191bacb5?crop=entropy&cs=srgb&fm=jpg&q=90",
    accent: "#D17A4D",
    marquee: ["Shubh Deepavali", "Handmade with love", "Festive gifting", "Light up their heart"],
    chips: ["Diyas & candles", "Warm ceramics", "Dried blooms"],
  },
  christmas: {
    name: "Christmas",
    tagline: "Wrapped with warmth.",
    intro: "Cosy, handmade Christmas gifting — soy candles, snug crochet and hand-thrown ceramics to make the season merry and heartfelt.",
    hero: "https://images.unsplash.com/photo-1481708426686-bad08e28e1c2?crop=entropy&cs=srgb&fm=jpg&q=90",
    accent: "#36453B",
    marquee: ["Merry Christmas", "Handmade & cosy", "Season of giving", "Wrapped with warmth"],
    chips: ["Soy candles", "Snug crochet", "Hand-thrown mugs"],
  },
  "durga-puja": {
    name: "Durga Puja",
    tagline: "Grace, handcrafted.",
    intro: "Celebrate the homecoming of the Goddess with handmade devotion — festive decor, artisan pottery and heartfelt keepsakes for pandal-hopping days and family evenings.",
    hero: "https://images.pexels.com/photos/27364815/pexels-photo-27364815.jpeg",
    accent: "#8C3D2B",
    marquee: ["Shubho Sharodiya", "Joy Maa Durga", "Handmade devotion", "Grace, handcrafted"],
    chips: ["Festive decor", "Artisan pottery", "Heartfelt keepsakes"],
  },
  dussehra: {
    name: "Dussehra",
    tagline: "Victory of good.",
    intro: "Mark the triumph of good over evil with handmade gifts that carry meaning — ceramics, candles and keepsakes made to be treasured, not tossed.",
    hero: "https://images.pexels.com/photos/9913866/pexels-photo-9913866.jpeg",
    accent: "#D17A4D",
    marquee: ["Happy Dussehra", "Good over evil", "Handmade with meaning", "Victory of good"],
    chips: ["Meaningful gifts", "Warm ceramics", "Festive candles"],
  },
  "karwa-chauth": {
    name: "Karwa Chauth",
    tagline: "For your moon.",
    intro: "Celebrate a love that fasts and prays — romantic bouquets, handcrafted jewellery and keepsakes as devoted as the vow behind this beautiful day.",
    hero: "https://images.pexels.com/photos/7176601/pexels-photo-7176601.jpeg",
    accent: "#8C3D2B",
    marquee: ["Happy Karwa Chauth", "For your beloved", "Handmade romance", "For your moon"],
    chips: ["Romantic bouquets", "Handmade jewels", "Love keepsakes"],
  },
  "bhai-dooj": {
    name: "Bhai Dooj",
    tagline: "For your first friend.",
    intro: "Honour the sibling bond with a gift as handmade as your shared memories — crochet keepsakes, resin art and little treasures for your brother or sister.",
    hero: "https://images.unsplash.com/photo-1716400128984-3681f2005d22?crop=entropy&cs=srgb&fm=jpg&q=90",
    accent: "#D17A4D",
    marquee: ["Happy Bhai Dooj", "For your sibling", "Handmade keepsakes", "For your first friend"],
    chips: ["Sibling keepsakes", "Crochet blooms", "Resin art"],
  },
  "new-year": {
    name: "New Year",
    tagline: "Begin it by hand.",
    intro: "Start the year with intention — handmade candles, ceramics and keepsakes to gift fresh beginnings to the people who make every year better.",
    hero: "https://images.pexels.com/photos/269415/pexels-photo-269415.jpeg",
    accent: "#36453B",
    marquee: ["Happy New Year", "Fresh beginnings", "Handmade with hope", "Begin it by hand"],
    chips: ["New-year candles", "Fresh ceramics", "Keepsakes"],
  },
  "makar-sankranti": {
    name: "Makar Sankranti",
    tagline: "Reach for the sky.",
    intro: "Ring in the harvest and the kites with handmade cheer — bright decor, warm ceramics and joyful little gifts for a sky full of colour.",
    hero: "https://images.pexels.com/photos/31358776/pexels-photo-31358776.jpeg",
    accent: "#D17A4D",
    marquee: ["Happy Sankranti", "Kites & sunshine", "Handmade cheer", "Reach for the sky"],
    chips: ["Bright decor", "Warm ceramics", "Joyful gifts"],
  },
  pongal: {
    name: "Pongal",
    tagline: "Overflowing with plenty.",
    intro: "Celebrate the harvest and gratitude with handmade warmth — artisan pottery, festive decor and heartfelt keepsakes for a home full of blessings.",
    hero: "https://images.pexels.com/photos/29226134/pexels-photo-29226134.jpeg",
    accent: "#8C3D2B",
    marquee: ["Happy Pongal", "Harvest & gratitude", "Handmade warmth", "Overflowing with plenty"],
    chips: ["Artisan pottery", "Festive decor", "Warm keepsakes"],
  },
  lohri: {
    name: "Lohri",
    tagline: "Warm as the fire.",
    intro: "Gather around the bonfire with handmade warmth — snug crochet, hand-poured candles and cosy keepsakes for the heartiest night of the year.",
    hero: "https://images.unsplash.com/photo-1759485186252-335429d617f6?crop=entropy&cs=srgb&fm=jpg&q=90",
    accent: "#D17A4D",
    marquee: ["Happy Lohri", "Around the bonfire", "Handmade & cosy", "Warm as the fire"],
    chips: ["Snug crochet", "Warm candles", "Cosy keepsakes"],
  },
  baisakhi: {
    name: "Baisakhi",
    tagline: "A golden harvest.",
    intro: "Celebrate the harvest and new beginnings with handmade joy — festive decor, warm ceramics and heartfelt gifts as golden as the fields.",
    hero: "https://images.pexels.com/photos/7891849/pexels-photo-7891849.jpeg",
    accent: "#36453B",
    marquee: ["Happy Baisakhi", "Golden harvest", "Handmade joy", "A golden harvest"],
    chips: ["Festive decor", "Warm ceramics", "Heartfelt gifts"],
  },
  eid: {
    name: "Eid",
    tagline: "Blessings, by hand.",
    intro: "Mark the joy of Eid with handmade elegance — hand-poured candles, artisan ceramics and keepsakes to gift the ones you cherish most.",
    hero: "https://images.pexels.com/photos/2233416/pexels-photo-2233416.jpeg",
    accent: "#36453B",
    marquee: ["Eid Mubarak", "Handmade elegance", "Season of joy", "Blessings, by hand"],
    chips: ["Elegant candles", "Artisan ceramics", "Keepsakes"],
  },
  onam: {
    name: "Onam",
    tagline: "A floral welcome.",
    intro: "Welcome the harvest festival of Kerala with handmade beauty — floral-inspired decor, warm ceramics and heartfelt keepsakes as vibrant as a pookalam.",
    hero: "https://images.pexels.com/photos/13752953/pexels-photo-13752953.jpeg",
    accent: "#D17A4D",
    marquee: ["Happy Onam", "Pookalam season", "Handmade beauty", "A floral welcome"],
    chips: ["Floral decor", "Warm ceramics", "Heartfelt keepsakes"],
  },
};

const EXTRA_NAMES = {
  "durga-puja": "Durga Puja", "dussehra": "Dussehra", "karwa-chauth": "Karwa Chauth",
  "bhai-dooj": "Bhai Dooj", "new-year": "New Year", "makar-sankranti": "Makar Sankranti",
  "pongal": "Pongal", "lohri": "Lohri", "baisakhi": "Baisakhi", "eid": "Eid", "onam": "Onam",
};
const FALLBACK_HERO = "https://images.unsplash.com/photo-1513151233558-d860c5398176?auto=format&fit=crop&w=1400&q=70";

function festConfig(slug) {
  if (FESTIVALS[slug]) return FESTIVALS[slug];
  const name = EXTRA_NAMES[slug];
  if (!name) return null;
  return {
    name, tagline: "Handmade, with heart.",
    intro: `Celebrate ${name} with one-of-a-kind handmade gifts and decor from independent makers across India — thoughtfully crafted for the occasion.`,
    hero: FALLBACK_HERO, accent: "#8C3D2B",
    marquee: [`Happy ${name}`, "Handmade with love", "Festive gifting", "Crafted for the season"],
    chips: ["Handmade decor", "Thoughtful gifts", "Made in India"],
  };
}

export default function FestivalStorefront() {
  const { slug } = useParams();
  const fest = festConfig(slug);
  const [products, setProducts] = useState([]);
  const [trending, setTrending] = useState([]);

  useEffect(() => {
    if (!fest) return;
    api.get("/products", { params: { festival: fest.name, limit: 12 } }).then(({ data }) => setProducts(data));
    api.get("/products/trending", { params: { festival: fest.name, limit: 4 } }).then(({ data }) => setTrending(data));
  }, [slug]); // eslint-disable-line

  if (!fest) return <Navigate to="/shop" replace />;

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden">
        <motion.img initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          src={optimg(fest.hero, 1400, 70)} alt={fest.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest/90 via-forest/40 to-forest/20" />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 pb-16 sm:pb-24 w-full">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="overline mb-5" style={{ color: fest.accent }}>
            <Sparkles className="w-4 h-4 inline mr-2" /> The {fest.name} Edit
          </motion.div>
          <h1 className="font-serif text-6xl sm:text-8xl leading-[0.9] tracking-tighter text-sand">
            <MaskLine delay={0.2}>{fest.name}.</MaskLine>
            <MaskLine delay={0.35}><span className="italic" style={{ color: fest.accent }}>{fest.tagline}</span></MaskLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="text-sand/80 max-w-lg mt-6 leading-relaxed">
            {fest.intro}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }} className="flex flex-wrap gap-2 mt-7">
            {fest.chips.map((c) => (
              <span key={c} className="text-sm px-4 py-2 rounded-full bg-sand/10 border border-sand/20 text-sand backdrop-blur">{c}</span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-line py-4" style={{ background: fest.accent }}>
        <Marquee speed={45} gradient={false}>
          {fest.marquee.concat(fest.marquee).map((t, i) => (
            <span key={i} className="font-serif italic text-2xl sm:text-3xl text-sand mx-8 tracking-tight">{t} <span className="mx-3">✦</span></span>
          ))}
        </Marquee>
      </div>

      {/* TRENDING THIS SEASON */}
      {trending.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-16 sm:pt-24" data-testid="festival-trending">
          <Reveal>
            <div className="flex items-end justify-between mb-8 gap-4">
              <div>
                <div className="overline mb-3 inline-flex items-center gap-2" style={{ color: fest.accent }}>
                  <Flame className="w-4 h-4" /> Trending this season
                </div>
                <h2 className="font-serif text-3xl sm:text-5xl tracking-tight">What everyone's<br /> gifting for {fest.name}</h2>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {trending.map((p, i) => (
              <div key={p.id} className="relative">
                <span className="absolute -top-2 -left-2 z-10 inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full text-sand shadow-sm" style={{ background: fest.accent }}>
                  <Flame className="w-3 h-3" /> #{i + 1}
                </span>
                <ProductCard product={p} index={i} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* PRODUCTS */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <Reveal>
          <div className="flex items-end justify-between mb-10 gap-4">
            <div>
              <div className="overline text-clay mb-3">Curated for {fest.name}</div>
              <h2 className="font-serif text-3xl sm:text-5xl tracking-tight">Gifts they'll remember</h2>
            </div>
            <Link to={`/shop?festival=${encodeURIComponent(fest.name)}`} className="hidden sm:inline-flex items-center gap-2 text-sm text-clay link-underline whitespace-nowrap">
              See all <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
        {products.length === 0 ? (
          <p className="text-mute py-12 text-center">Makers are still adding {fest.name} gifts — check back soon.</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
